import {
  Component,
  OnInit,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
  NgZone,
} from '@angular/core';
import { AuthenticationService } from './services/authentication.service';
import { auditTime, fromEvent } from 'rxjs';
import { App } from '@capacitor/app';
import { ToolsService } from './services/tools.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpriteManager } from 'src/app/services/mapbox/utils';
import { Router, RouterOutlet } from '@angular/router';
import 'zone.js/plugins/zone-patch-rxjs';
import { Title } from '@angular/platform-browser';
import { environment } from '../environments/environment';
import { resolveBrandScheme } from './utils/brand-context';

const URL_SHORT = resolveBrandScheme();

@Component({
  selector: 'app-root',
  template: `<router-outlet></router-outlet>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private spriteService = inject(SpriteManager);
  private ngZone = inject(NgZone);

  constructor(
    private authService: AuthenticationService,
    private tools: ToolsService,
    private router: Router,
    private titleService: Title
  ) {
    this.setupUrlOpenListener();

    // Fix pour l'affichage chrome sous iphone (et potentiellement d'autres)
    this.fixDisplayVhToPx();
    this.spriteService.init();
  }

  /**
   * Permet de gérer l'ouverture de l'application sur mobile via un lien externe
   * L'url est de la forme ://..
   * L'url est utilisée pour gérer les redirections pour la connexion via "email magic link"
   */
  setupUrlOpenListener() {
    if (this.tools.isMobile()) {
      App.addListener('appUrlOpen', (data) => {
        const url = data.url.replace(URL_SHORT, '');

        // Ex : xxxx://login?error=invalid-ticket&errorDescription=Invalid+or+expired+verification+ticket
        const pageStr = url.split('?')[0];
        const page = pageStr === 'login' ? '' : pageStr;
        const queryParams = url.split('?')[1];
        const queryParamsObj = queryParams
          ? queryParams
              .split('&')
              .reduce((acc: { [key: string]: string }, param) => {
                const [key, value] = param.split('=');
                acc[key] = value;
                return acc;
              }, {})
          : {};

        this.router.navigate(['/' + page], {
          queryParams: {
            returnUrl: queryParams,
            ...queryParamsObj,
          },
          replaceUrl: true,
        });
      });
    }
  }

  public ngOnInit(): void {
    // Définir le titre de l'app selon l'environnement
    this.titleService.setTitle(environment.appTitle);
    
    this.authService.init().then(() => {
      this.initializeApp();
    });
  }

  public initializeApp() {
    App.addListener('backButton', () => {});
  }

  private fixDisplayVhToPx() {
    this.updateViewportUnit();
    this.ngZone.runOutsideAngular(() => {
      fromEvent(window, 'resize')
        .pipe(auditTime(100), takeUntilDestroyed(this.destroyRef))
        // eslint-disable-next-line rxjs/no-ignored-subscription -- takeUntilDestroyed is used
        .subscribe(() => {
          this.updateViewportUnit();
        });
    });
  }

  public updateViewportUnit() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }
}
